/**
 * Policy pages (/policies/…): lays out the policy text that Shopify renders itself
 * (snippets/olin-policy.liquid puts the band, the styles and this script around it).
 * - The «Версія договору для звичайних людей» heading and its list become the «Коротко» card,
 *   above the formal text; a quote in the text already is that card.
 * - The address under «Адреса для повернення» becomes a cream card; questions get a mark.
 * - Clauses «1.1. …» get their number in a column of its own.
 * - A long policy (five sections or more) gets its contents: beside the text on computers,
 *   a fold-out list above it on phones, with the section in view marked.
 * - The requisites become a dark card with copy buttons for the tax number and the IBAN.
 * - «Дивіться також: …» links become buttons.
 */

const top = document.querySelector('.olin-policy-top');
const container = document.querySelector('.shopify-policy__container');
const body = container?.querySelector('.shopify-policy__body');
const rte = body?.querySelector('.rte');

if (top instanceof HTMLElement && container && body && rte) {
  const [tocLabel, briefLabel, copyLabel, copiedLabel, sellerLabel] = (top.dataset.labels || '').split('|');

  /** @param {Element | null} element */
  const text = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();
  const children = () => Array.from(rte.children);

  // «Коротко» from the plain-language version of the terms
  const plainHeading = children().find(
    (element) => element.tagName === 'H2' && /звичайних людей|обычных людей/i.test(text(element))
  );
  const plainList = plainHeading?.nextElementSibling;
  if (plainHeading && plainList?.tagName === 'UL') {
    const card = document.createElement('blockquote');
    const label = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = briefLabel || '';
    label.append(strong);
    const title = document.createElement('p');
    title.textContent = text(plainHeading);
    card.append(label, title, plainList);

    // The long formal paragraph before it goes under the formal heading
    const formal = plainHeading.previousElementSibling;
    const formalHeading = plainHeading.nextElementSibling;
    if (formal?.tagName === 'P' && !formal.querySelector('em') && text(formal).length > 300) {
      formal.before(card);
      if (formalHeading?.tagName === 'H2') formalHeading.after(formal);
    } else {
      plainHeading.before(card);
    }
    plainHeading.remove();
  }

  // The return address as a card, the questions with a mark
  let inQuestions = false;
  for (const element of children()) {
    if (element.tagName === 'H2') inQuestions = /питання|вопрос/i.test(text(element));
    if (element.tagName === 'H3' && inQuestions) element.classList.add('olin-policy-question');
    if (element.tagName === 'H3' && /^адрес/i.test(text(element))) {
      element.nextElementSibling?.tagName === 'P' && element.nextElementSibling.classList.add('olin-policy-address');
    }
  }

  // Clauses: «1.1. …» with the number in a column
  for (const paragraph of rte.querySelectorAll(':scope > p')) {
    const first = paragraph.firstChild;
    const match = first?.nodeType === Node.TEXT_NODE ? /^(\d+\.\d+\.)\s+/.exec(first.textContent || '') : null;
    if (!match || !first) continue;
    first.textContent = (first.textContent || '').slice(match[0].length);
    const number = document.createElement('span');
    number.className = 'olin-policy-clause__num';
    number.textContent = match[1];
    const clause = document.createElement('span');
    clause.append(...paragraph.childNodes);
    paragraph.append(number, clause);
    paragraph.classList.add('olin-policy-clause');
  }

  // Requisites: «Label: value» lines as a card, copy buttons for the tax number and the IBAN
  const requisitesHeading = children().find(
    (element) => element.tagName === 'H2' && /реквізит|реквизит/i.test(text(element))
  );
  const requisites = requisitesHeading?.nextElementSibling;
  if (requisites?.tagName === 'P') {
    const list = document.createElement('dl');
    list.className = 'olin-policy-requisites';
    for (const line of requisites.innerHTML.split(/<br\s*\/?>/i)) {
      const holder = document.createElement('div');
      holder.innerHTML = line;
      const lineText = text(holder);
      if (!lineText) continue;
      const colon = lineText.indexOf(':');
      const label = colon > 0 && colon < 20 ? lineText.slice(0, colon) : sellerLabel || '';
      const value = colon > 0 && colon < 20 ? lineText.slice(colon + 1).trim() : lineText;
      const row = document.createElement('div');
      row.className = 'olin-policy-requisites__row';
      const term = document.createElement('dt');
      term.textContent = label;
      const definition = document.createElement('dd');
      const isIban = /^UA\d{27}$/.test(value.replace(/\s/g, ''));
      const shown = document.createElement('span');
      shown.textContent = isIban ? value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim() : value;
      definition.append(shown);
      if (isIban || /^(ІПН|ИНН)$/i.test(label)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = copyLabel || '';
        button.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(value.replace(/\s/g, ''));
            button.textContent = copiedLabel || '';
            setTimeout(() => (button.textContent = copyLabel || ''), 2000);
          } catch {
            // Copying is not allowed here; the value stays selectable
          }
        });
        definition.append(button);
      }
      row.append(term, definition);
      list.append(row);
    }
    requisites.replaceWith(list);
  }

  // «Дивіться також: links» as buttons
  for (const paragraph of rte.querySelectorAll(':scope > p')) {
    if (!/^(дивіться також|смотрите также)/i.test(text(paragraph))) continue;
    const links = Array.from(paragraph.querySelectorAll('a'));
    if (!links.length) continue;
    const label = document.createElement('span');
    label.textContent = text(paragraph).split(':')[0] + ':';
    paragraph.replaceChildren(label, ...links);
    paragraph.classList.add('olin-policy-also');
  }

  // Contents of a long policy: its numbered sections, the card and the headings after them
  const sections = Array.from(rte.querySelectorAll(':scope > h3'));
  if (sections.length >= 5) {
    const lastSection = sections[sections.length - 1];
    const targets = [
      ...(rte.querySelector(':scope > blockquote') ? [rte.querySelector(':scope > blockquote')] : []),
      ...sections,
      ...children().filter(
        (element) =>
          element.tagName === 'H2' && lastSection.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
      ),
    ].filter((element) => element instanceof HTMLElement);

    const nav = document.createElement('nav');
    nav.className = 'olin-policy-toc';
    nav.setAttribute('aria-label', tocLabel || '');
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    summary.textContent = tocLabel || '';
    const list = document.createElement('ol');
    /** @type {Map<Element, HTMLAnchorElement>} */
    const links = new Map();
    targets.forEach((target, index) => {
      if (!target.id) target.id = `olin-policy-${index + 1}`;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${target.id}`;
      link.textContent = target.tagName === 'BLOCKQUOTE' ? briefLabel || '' : text(target);
      item.append(link);
      list.append(item);
      links.set(target, link);
    });
    details.append(summary, list);
    nav.append(details);
    body.prepend(nav);
    container.classList.add('olin-policy-has-toc');
    top.classList.add('olin-policy-has-toc');

    // Open beside the text on computers, folded above it on phones
    const wide = window.matchMedia('(min-width: 990px)');
    const syncOpen = () => (details.open = wide.matches);
    syncOpen();
    wide.addEventListener('change', syncOpen);

    // The section in view
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          for (const link of links.values()) link.removeAttribute('aria-current');
          links.get(entry.target)?.setAttribute('aria-current', 'true');
        }
      },
      { rootMargin: '-110px 0px -70% 0px' }
    );
    for (const target of targets) observer.observe(target);
  }
}
